import { CommonModule } from '@angular/common';
import {
  AfterContentInit,
  Component,
  ComponentRef,
  HostBinding,
  Input,
  isDevMode,
  OnChanges,
  OnDestroy,
  Type,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BehaviorSubject, from, Observable, of, throwError } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';

import { PTypes } from '@digital-platform/rs-cms/domain';
import { PTYPES_ROUTES } from '../routes';

@UntilDestroy()
@Component({
  selector: 'digital-platform-dynamic-content-wrapper',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './dynamic-content-wrapper.component.html',
  styleUrls: ['./dynamic-content-wrapper.component.scss'],
})
export class DynamicContentWrapperComponent
  implements AfterContentInit, OnChanges, OnDestroy
{
  @HostBinding('class') className: string | null = null;
  @Input() public config: PTypes | null = null;

  public config$: BehaviorSubject<PTypes | null> =
    new BehaviorSubject<PTypes | null>(null);

  @ViewChild('dynamic', { static: true, read: ViewContainerRef })
  public dynamicContainer!: ViewContainerRef;

  private componentInstance: ComponentRef<any> | null = null;
  private isDevMode = isDevMode();

  constructor() {
    // No-op timeout to ensure SSR works correctly. This is a band-aid only
    setTimeout(() => {}, 350);
  }

  ngOnChanges() {
    if (this.config) {
      this.config$.next(this.config);
    } else {
      this.config$.next(null);
    }
  }

  ngAfterContentInit() {
    this.config$
      .pipe(
        untilDestroyed(this),
        mergeMap((config): Observable<[PTypes | null, Type<PTypes> | null]> => {
          if (config) {
            return this.getComponentByName(config.type).pipe(
              map((component) => [config, component])
            );
          }

          return of([config, null]);
        })
      )
      .subscribe({
        next: ([config, component]) => {
          this.destroyComponentIfPresent();

          if (component && config) {
            this.createAndPopulateComponent(component, config);
          }
        },
        error: (error) => {
          console.error('Error creating dynamic component', { error });
        },
      });
  }

  ngOnDestroy(): void {
    this.destroyComponentIfPresent();
  }

  private destroyComponentIfPresent(): void {
    if (this.componentInstance) {
      this.componentInstance.destroy();
    }
  }

  private createAndPopulateComponent(component: Type<any>, config: PTypes) {
    this.componentInstance =
      this.dynamicContainer.createComponent<any>(component);

    if (component.name === 'UnknownPTypeComponent') {
      console.warn(`Caught Unknown PType '${config.type}'`, { config });

      this.componentInstance.instance['config'] = config;
    }

    for (const prop of Object.keys(config)) {
      if (Object.prototype.hasOwnProperty.call(config, prop)) {
        if (prop === 'angularConfig' && typeof config[prop] === 'string') {
          try {
            this.componentInstance.instance[prop] = JSON.parse(
              config[prop] as string
            );
          } catch (e) {
            console.error(`Unable to parse ${config[prop]} as JSON.`);
          }
        } else {
          this.componentInstance.instance[prop] = config[prop as keyof PTypes];
        }
      }
    }

    if (
      this.componentInstance.instance['ngOnChanges'] &&
      typeof this.componentInstance.instance['ngOnChanges'] === 'function'
    ) {
      this.componentInstance.instance.ngOnChanges();
    }

    this.className =
      this.config$.value?.behaviorSettings?.layout_paragraphs?.region ?? '';
  }

  private getComponentByName(name: string): Observable<Type<PTypes>> {
    let route = PTYPES_ROUTES.find((r) => r.path === name);

    if (route && route.loadComponent) {
      return from(route.loadComponent());
    }

    // load unknown ptype component if nonprod
    if (this.isDevMode) {
      route = PTYPES_ROUTES.find((r) => r.path === 'unknown');
      if (route && route.loadComponent) {
        return from(route.loadComponent());
      }
    }

    return throwError(() => new Error(`No component found for ${name}`));
  }
}
